import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from 'chart.js';
import { Box, Typography } from '@mui/material';
import { useAppSelector } from '../../../../../hooks';
import { getSubmissionsV2State } from '../../../../../store/V2/SubmissionsSlice';
import { TP_COLORS } from '../../../../../constants';

// Register required chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const initialData: ChartData<'bar'> = {
  labels: [],
  datasets: [
    {
      label: 'Score',
      data: [],
      backgroundColor: '#14466D',
      borderRadius: 5,
    },
  ],
};

const HorizontalBarChart: React.FC = () => {
  // State
  const [data, setData] = useState(initialData);

  // Store
  const { courseSubmissionsData } = useAppSelector(getSubmissionsV2State);

  useEffect(() => {
    if (courseSubmissionsData?.groupedSubmissions) {
      const topicMap = new Map<string, number>();

      courseSubmissionsData.groupedSubmissions.forEach((submission) => {
        topicMap.set(submission.topicName, submission.averageScore);
      });

      // Extract unique labels and scores
      const labels = Array.from(topicMap.keys());
      const scores = Array.from(topicMap.values());

      setData({
        labels,
        datasets: [
          {
            label: 'Score',
            data: scores,
            backgroundColor: TP_COLORS.blue.dark,
            borderRadius: 3,
            barThickness: 18,
            // barPercentage: 0.5, // Adjusts bar width
            // categoryPercentage: 0.5,
          },
        ],
      });
    }
  }, [courseSubmissionsData]);

  const options: ChartOptions<'bar'> = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: false,
        // beginAtZero: true,
        max: 20,
      },
      y: {
        stacked: false,
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          // usePointStyle: false,
          boxWidth: 0,
        },
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (tooltipItem) => {
            const value = tooltipItem.raw;
            return `Class Average: ${value}/20`;
          },
        },
      },
    },
  };

  return (
    <>
      <Box>
        <Typography fontSize="18px" color={TP_COLORS.gray.darken}>
          Average Scores Per Topic
        </Typography>
        <Typography fontSize="14px" color="#647488">
          Comparison of Student Scores to Class Average
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          marginTop: '32px',
          minHeight: '400px',
          height: `${50 * (data.labels?.length || 10)}px`,
        }}
      >
        <Bar data={data} options={options} />
      </Box>
    </>
  );
};

export default HorizontalBarChart;
